/**
 * /plaza — HustlePlaza Coming-Soon Storefront
 *
 * Shopify-style mock storefront showcasing the cooperative-buying model.
 *
 * v2 (Sun 01 Jun 2026) — restructured for dropshipper review:
 *   • Master Group → Sub-Category sidebar (accordion, 19 master groups)
 *   • Grid / Table view toggle (smart default: table if >6 results, grid otherwise)
 *   • Mobile-first: filter drawer slides from the left, sticky toolbar
 *   • Go-live moved August → June 2026
 *
 * Backend hooks:
 *   POST /api/directory/plaza/waitlist        — consumer signup → Mongo + HustleCRM
 *   POST /api/directory/plaza/seller-interest — vendor signup  → Mongo + HustleCRM
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import {
  Search, ShoppingCart, Star, Plus, X, Minus, ArrowRight,
  Truck, HeartHandshake, Percent, Store, ChevronDown, ChevronRight,
  LayoutGrid, Rows3, SlidersHorizontal, Sun, Moon, Copy, CheckCircle2,
  TrendingUp, MapPin, Flame, Mail,
} from "lucide-react";
import { PLAZA_PRODUCTS, MASTER_GROUPS } from "./plazaProducts";
import JoinGateModal from "@/components/verify/JoinGateModal";
import { useAuth } from "@/context/AuthContext";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import HustlePointsPromoBanner from "@/components/HustlePointsPromoBanner";
import "./HustlePlazaPage.css";

const LOGO_LIGHT =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/d6gkfgwo_Local%20Hustle%20Logo%20-%20PLAZA%20LIGHT.png";
const LOGO_DARK =
  "https://customer-assets.emergentagent.com/job_mobile-resto/artifacts/ecrffi9b_Local%20Hustle%20Logo%20-%20PLAZA%20DARK.png";

const THEME_KEY = "hpz-theme";

/* AU dollar formatter — 1,234.56 with thousands commas. */
const fmtMoney = (n) =>
  Number(n).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const SORT_OPTIONS = [
  { id: "popular",   label: "Most Popular" },
  { id: "price_asc", label: "Price: Low → High" },
  { id: "price_desc",label: "Price: High → Low" },
  { id: "rating",    label: "Top Reviews" },
  { id: "discount",  label: "Best Discount" },
  { id: "newest",    label: "Newest" },
];

const FAQ_ITEMS = [
  {
    q: "What is HustlePlaza, exactly?",
    a: "HustlePlaza is a cooperative-buying marketplace from the HustlePlate group. Members pool their orders so suppliers can offer wholesale-tier pricing across 19 categories — from premium small-batch coffee to whitegoods, fashion, tools, baby gear and pet care. The kind of catalogue you'd find at a great department store, but at member-pooled pricing."
  },
  {
    q: "How does cooperative buying actually work?",
    a: "When you order, your basket joins thousands of other members' baskets each week. We negotiate one bulk order to each supplier, which unlocks pricing they don't offer to single buyers. You save 20–50%, the supplier ships fewer, larger deliveries (lower cost for them too), and the planet sees less last-mile freight."
  },
  {
    q: "When does HustlePlaza go live?",
    a: "Founding-member orders open the week of June 2nd, 2026. Joining the waitlist now gets you (a) early access 48 hours before public launch and (b) a $25 founding-member credit on your first order."
  },
  {
    q: "Is HustlePlaza only for hospitality businesses?",
    a: "No — it's open to both consumers and businesses. Households save money on premium pantry, whitegoods, fashion, and homewares. Hospitality venues (cafés, restaurants, salons in the HustlePlate Directory) get an additional tier of pricing on commercial-quantity buys."
  },
  {
    q: "Where does HustlePlaza deliver?",
    a: "Initial launch: SE Queensland (Brisbane, Gold Coast, Sunshine Coast, Toowoomba, Ipswich). Expanding to Sydney + Melbourne metro by Q4 2026, then the rest of Australia in 2027 as we onboard regional fulfilment partners."
  },
  {
    q: "I make a product — how do I sell on HustlePlaza?",
    a: "Tap the \"Sell on HustlePlaza\" button. We're onboarding ~120 founding vendors before launch. Priority is given to Australian small-batch producers, ethically sourced imports, and B-Corp-certified suppliers."
  },
  {
    q: "Do members earn loyalty rewards?",
    a: "Yes. Every dollar spent earns Plaza Points that can be redeemed against future orders, exclusive member drops, and partner experiences across the HustlePlate ecosystem. Points and tier rules are published in your account dashboard once orders go live."
  },
];

const ALL_CATEGORY_TAGS = MASTER_GROUPS.flatMap((g) => g.subs);

/**
 * Value chips — filter the catalogue by shared-values tags. Each chip
 * matches one or more product tags (case-insensitive). Ordering here
 * controls the pill row order under the search bar.
 */
const VALUE_CHIPS = [
  { key: "aus_owned",   label: "Australian owned",  match: ["australian-owned", "aus-owned", "made-in-australia", "australian-made", "local", "australian"] },
  { key: "handmade",    label: "Handmade",          match: ["handmade", "artisan", "craft", "small-batch"] },
  { key: "family",      label: "Family run",        match: ["family", "family-run", "family-owned"] },
  { key: "ethical",     label: "Ethically sourced", match: ["ethical", "fair-trade", "b-corp"] },
  { key: "organic",     label: "Organic",           match: ["organic", "certified-organic"] },
  { key: "vegan",       label: "Vegan",             match: ["vegan", "plant-based"] },
];

const matchesValueChip = (product, chipKey) => {
  if (!chipKey) return true;
  const chip = VALUE_CHIPS.find((c) => c.key === chipKey);
  if (!chip) return true;
  const tags = (product.tags || []).map((t) => String(t).toLowerCase());
  return chip.match.some((m) => tags.includes(m));
};

/**
 * Derive which VALUE_CHIPS apply to a specific product — used to
 * render "Australian owned / Handmade" badges on the product card.
 */
const valuesFor = (product) => {
  const tags = (product.tags || []).map((t) => String(t).toLowerCase());
  return VALUE_CHIPS.filter((c) => c.match.some((m) => tags.includes(m)));
};

export default function HustlePlazaPage() {
  /* ─── state ─────────────────────────────────────────────────── */
  const [query, setQuery]           = useState("");
  // category = sub_category name OR a master group name (prefixed "G:")
  const [filter, setFilter]         = useState({ type: "all" });
  const [valueChip, setValueChip]   = useState(null); // "aus_owned" | "handmade" | ...
  const [sort, setSort]             = useState("popular");
  const [view, setView]             = useState("grid"); // "grid" | "table" — auto-switches below
  const [viewLocked, setViewLocked] = useState(false);  // user picked one — don't auto-switch
  const [cart, setCart]             = useState([]);
  const [cartOpen, setCartOpen]     = useState(false);
  const [joinGateOpen, setJoinGateOpen] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [faqOpen, setFaqOpen]       = useState(-1);
  const [filtersOpen, setFiltersOpen] = useState(false); // mobile drawer
  // Expanded master groups in the sidebar accordion
  const [expanded, setExpanded]     = useState(new Set(["Groceries / Food / Pantry"]));
  // Product-detail modal (null = closed; product object = open)
  const [detail, setDetail]         = useState(null);

  // ── Conversion-system state (live counter, scarcity, referral) ─────
  const [settings, setSettings]     = useState(null);    // server settings
  const [stats, setStats]           = useState(null);    // live counts
  const [incomingRef, setIncomingRef] = useState(null);  // ?ref= URL param

  // Waitlist response carries the new member's ref_code + credit awarded.
  const [wlResponse, setWlResponse] = useState(null);
  const [refCopied, setRefCopied]   = useState(false);

  // Cart-abandon "save for launch" mini-modal state.
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [saveEmail, setSaveEmail]   = useState("");
  const [saveBusy, setSaveBusy]     = useState(false);
  const [saveDone, setSaveDone]     = useState(false);

  // Capture ?ref= once on mount, then strip it from the URL for cleanliness.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    const r = url.searchParams.get("ref");
    if (r) {
      setIncomingRef(r.toLowerCase().slice(0, 32));
      url.searchParams.delete("ref");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  // Fetch settings + live stats once on mount; refresh stats every 30s.
  useEffect(() => {
    let active = true;
    const base = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
    const load = async () => {
      try {
        const [s, t] = await Promise.all([
          fetch(`${base}/api/directory/plaza/settings`).then((r) => r.json()),
          fetch(`${base}/api/directory/plaza/stats`).then((r) => r.json()),
        ]);
        if (!active) return;
        setSettings(s);
        setStats(t);
      } catch (e) { /* ignore — falls back to empty UI */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { active = false; clearInterval(id); };
  }, []);
  // Theme — persisted to localStorage. First-time default = dark.
  const [theme, setTheme]           = useState(() => {
    if (typeof window === "undefined") return "dark";
    return localStorage.getItem(THEME_KEY) || "dark";
  });

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(THEME_KEY, theme);
  }, [theme]);

  // Live master-group pill counts fetched from the backend. Falls
  // back to the curated static counts on the fetch failing so the
  // pill row never renders empty.
  const [catGroups, setCatGroups] = useState(null);

  useEffect(() => {
    let alive = true;
    fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/plaza/master-group-counts`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (alive && d && Array.isArray(d.groups) && d.groups.length) {
          setCatGroups(d.groups);
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  // Waitlist form state
  const [wlEmail, setWlEmail]       = useState("");
  const [wlBusiness, setWlBusiness] = useState("");
  const [wlSuburb, setWlSuburb]     = useState("");
  const [wlCats, setWlCats]         = useState([]);
  const [wlBuyer, setWlBuyer]       = useState("consumer");
  const [wlBusy, setWlBusy]         = useState(false);
  const [wlOk, setWlOk]             = useState(false);
  const [wlErr, setWlErr]           = useState("");

  /* ─── fuse instance ─────────────────────────────────────────── */
  const fuse = useMemo(() => new Fuse(PLAZA_PRODUCTS, {
    keys: ["name", "brand", "master_group", "sub_category", "tags"],
    threshold: 0.36,
    ignoreLocation: true,
  }), []);

  /* ─── filtered + sorted list ────────────────────────────────── */
  const filtered = useMemo(() => {
    let list = query.trim()
      ? fuse.search(query).map((r) => r.item)
      : [...PLAZA_PRODUCTS];

    if (filter.type === "group") {
      list = list.filter((p) => p.master_group === filter.value);
    } else if (filter.type === "sub") {
      list = list.filter((p) => p.sub_category === filter.value);
    }

    if (valueChip) {
      list = list.filter((p) => matchesValueChip(p, valueChip));
    }

    const cmp = {
      popular:    (a, b) => Number(b.popular) - Number(a.popular) || b.reviews - a.reviews,
      price_asc:  (a, b) => a.price - b.price,
      price_desc: (a, b) => b.price - a.price,
      rating:     (a, b) => b.rating - a.rating || b.reviews - a.reviews,
      discount:   (a, b) => {
        const da = a.was ? (a.was - a.price) / a.was : 0;
        const db = b.was ? (b.was - b.price) / b.was : 0;
        return db - da;
      },
      newest:     (a, b) => Number(b.is_new) - Number(a.is_new),
    };
    list.sort(cmp[sort] || cmp.popular);
    return list;
  }, [query, filter, sort, fuse, valueChip]);

  /* Auto view: if <= 6 results, default to grid; otherwise table.
     User can lock with the explicit toggle. */
  useEffect(() => {
    if (viewLocked) return;
    setView(filtered.length <= 6 ? "grid" : "table");
  }, [filtered.length, viewLocked]);

  /* counts per group + sub */
  const counts = useMemo(() => {
    const groupCounts = {};
    const subCounts = {};
    PLAZA_PRODUCTS.forEach((p) => {
      groupCounts[p.master_group] = (groupCounts[p.master_group] || 0) + 1;
      subCounts[p.sub_category]   = (subCounts[p.sub_category] || 0) + 1;
    });
    return { groupCounts, subCounts, total: PLAZA_PRODUCTS.length };
  }, []);

  /* top 4 featured */
  const topProducts = useMemo(
    () => PLAZA_PRODUCTS.filter((p) => p.popular).slice(0, 4),
    []
  );

  /* Featured Vendors — top 6 brands ranked by total reviews across
     their popular products. Each entry carries: brand name, top
     product image, avg rating, product count, and its own top pick
     for a "See picks" deep-link into the catalogue. */
  const featuredVendors = useMemo(() => {
    const byBrand = new Map();
    PLAZA_PRODUCTS.forEach((p) => {
      if (!p.brand) return;
      const b = byBrand.get(p.brand) || {
        brand: p.brand, products: [], totalReviews: 0, ratingSum: 0,
      };
      b.products.push(p);
      b.totalReviews += p.reviews || 0;
      b.ratingSum += p.rating || 0;
      byBrand.set(p.brand, b);
    });
    return Array.from(byBrand.values())
      .filter((b) => b.products.length >= 2)         // real vendors only
      .map((b) => ({
        brand:      b.brand,
        img:        (b.products.find((p) => p.popular) || b.products[0]).img,
        avgRating:  (b.ratingSum / b.products.length),
        count:      b.products.length,
        topPick:    (b.products.slice().sort((a, c) => c.reviews - a.reviews)[0]),
        values:     Array.from(new Set(
          b.products.flatMap((p) => valuesFor(p).map((v) => v.key)),
        )).slice(0, 3),
      }))
      .sort((a, b) => b.avgRating - a.avgRating || b.count - a.count)
      .slice(0, 6);
  }, []);

  /* count products matching each value chip (for pill labels) */
  const valueCounts = useMemo(() => {
    const m = {};
    VALUE_CHIPS.forEach((c) => {
      m[c.key] = PLAZA_PRODUCTS.filter((p) => matchesValueChip(p, c.key)).length;
    });
    return m;
  }, []);

  /* ─── cart helpers ──────────────────────────────────────────── */
  // Non-member gate: if the visitor isn't logged in, show the JoinGate
  // popup instead of adding the item. We stash the intent in
  // sessionStorage so we can auto-add it right after they finish
  // signing up.
  const addToCart = (id) => {
    if (!user) {
      try {
        window.sessionStorage.setItem(
          "hp_pending_cart_item",
          JSON.stringify({ id, ts: Date.now() }),
        );
      } catch { /* silent */ }
      setJoinGateOpen(true);
      return;
    }
    setCart((c) => {
      const x = c.find((r) => r.id === id);
      return x ? c.map((r) => r.id === id ? { ...r, qty: r.qty + 1 } : r) : [...c, { id, qty: 1 }];
    });
    setCartOpen(true);
  };
  const setQty = (id, delta) =>
    setCart((c) =>
      c.flatMap((r) => r.id === id
        ? (r.qty + delta <= 0 ? [] : [{ ...r, qty: r.qty + delta }])
        : [r]
      )
    );

  const cartTotal = cart.reduce((sum, r) => {
    const p = PLAZA_PRODUCTS.find((x) => x.id === r.id);
    return sum + (p ? p.price * r.qty : 0);
  }, 0);
  const cartCount = cart.reduce((s, r) => s + r.qty, 0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { setCartOpen(false); setFiltersOpen(false); setDetail(null); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ─── waitlist submit ───────────────────────────────────────── */
  const submitWaitlist = async (e) => {
    e.preventDefault();
    setWlErr("");
    if (!wlEmail || !wlBusiness || !wlSuburb) {
      setWlErr("Please fill all required fields.");
      return;
    }
    setWlBusy(true);
    try {
      const endpoint = wlBuyer === "seller"
        ? "/api/directory/plaza/seller-interest"
        : "/api/directory/plaza/waitlist";
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: wlEmail,
          business_name: wlBusiness,
          suburb: wlSuburb,
          categories: wlCats,
          ref: incomingRef || undefined,
        }),
      });
      if (!res.ok) throw new Error("Server error");
      const data = await res.json();
      setWlResponse(data);
      setWlOk(true);
      // Refresh public stats so the counter ticks up immediately.
      try {
        const t = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/directory/plaza/stats`).then((r) => r.json());
        setStats(t);
      } catch (e) { /* ignore */ }
    } catch (err) {
      setWlErr("Could not submit. Please try again in a moment.");
    } finally {
      setWlBusy(false);
    }
  };

  const toggleCat = (c) =>
    setWlCats((arr) => arr.includes(c) ? arr.filter((x) => x !== c) : [...arr, c]);

  /* ─── cart-abandon save-for-later ───────────────────────────── */
  const saveBasketForLater = async (e) => {
    e.preventDefault();
    if (!saveEmail || !saveEmail.includes("@")) return;
    setSaveBusy(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/directory/plaza/cart-save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: saveEmail, items: cart }),
      });
      if (res.ok) setSaveDone(true);
    } catch (err) { /* swallow */ }
    finally { setSaveBusy(false); }
  };

  /* Build the shareable referral link from the response. */
  const refLink = wlResponse?.ref_code
    ? `${typeof window !== "undefined" ? window.location.origin : ""}/plaza?ref=${wlResponse.ref_code}`
    : "";
  const copyRefLink = () => {
    if (!refLink) return;
    navigator.clipboard?.writeText(refLink);
    setRefCopied(true);
    setTimeout(() => setRefCopied(false), 2200);
  };

  /* ─── sidebar accordion helpers ─────────────────────────────── */
  const toggleGroup = (name) => {
    setExpanded((s) => {
      const n = new Set(s);
      n.has(name) ? n.delete(name) : n.add(name);
      return n;
    });
  };
  const chooseGroup = (name) => {
    setFilter({ type: "group", value: name });
    setExpanded((s) => new Set([...s, name]));
    setFiltersOpen(false);
  };
  const chooseSub = (sub) => {
    setFilter({ type: "sub", value: sub });
    setFiltersOpen(false);
  };
  const chooseAll = () => {
    setFilter({ type: "all" });
    setFiltersOpen(false);
  };

  const filterLabel = filter.type === "all"
    ? "All Products"
    : filter.type === "group"
      ? filter.value
      : filter.value;

  /* ─── render ────────────────────────────────────────────────── */
  return (
    <div className="hpz-root" data-theme={theme} data-testid="plaza-root">
      <PlatformHeader
        brand={{ name: "HustlePlaza", to: "/plaza" }}
        showBookCta={false}
        showCart={false}
      />
      {/* ── Top Bar ─────────────────────────────────────────────── */}
      <div className="hpz-topbar" data-testid="plaza-topbar">
        <a href="/plaza" className="hpz-topbar__brand">
          <img
            src={theme === "light" ? LOGO_LIGHT : LOGO_DARK}
            alt="HustlePlaza"
            className="hpz-topbar__logo"
          />
        </a>
        <nav className="hpz-topbar__nav">
          <a href="#why">Why Co-op?</a>
          <a href="#shop">Shop</a>
          <a href="#faq">FAQ</a>
          <a href="#sellers">Sell with Us</a>
          <a href="/plaza/terms" data-testid="plaza-topbar-terms">Terms</a>
          <div className="hpz-nav-dropdown" data-testid="plaza-nav-demo-dropdown">
            <button className="hpz-nav-dropdown__trigger" type="button">
              Demo ▾
            </button>
            <div className="hpz-nav-dropdown__menu">
              <a href="/demo"  data-testid="nav-demo-playbook">Full playbook</a>
              <a href="/plate" data-testid="nav-demo-plate">HustlePlate pricing</a>
              <div className="hpz-nav-dropdown__divider" />
              <a href="/pitch/investor"  data-testid="nav-demo-1">1. Investor pitch</a>
              <a href="/pitch/diner"     data-testid="nav-demo-2">2. Diner pitch</a>
              <a href="/pitch/owner"     data-testid="nav-demo-3">3. Owner pitch</a>
              <a href="/sales"           data-testid="nav-demo-4">4. Savings calculator</a>
              <a href="/plate"           data-testid="nav-demo-5">5. HustlePlate tiers</a>
              <a href="/pitch/salon"     data-testid="nav-demo-6">6. Salon pitch</a>
              <a href="/directory/au"    data-testid="nav-demo-7">7. AU directory</a>
              <a href="/plaza"           data-testid="nav-demo-8">8. HustlePlaza</a>
              <a href="/coffee-commune"  data-testid="nav-demo-9">9. Coffee Commune</a>
            </div>
          </div>
        </nav>
        <div className="hpz-topbar__right">
          <div className="hpz-theme-toggle" role="group" aria-label="Colour mode">
            <button
              className={theme === "light" ? "on" : ""}
              onClick={() => setTheme("light")}
              title="Light mode"
              aria-label="Switch to light mode"
              data-testid="plaza-theme-light"
            >
              <Sun size={16} />
            </button>
            <button
              className={theme === "dark" ? "on" : ""}
              onClick={() => setTheme("dark")}
              title="Dark mode"
              aria-label="Switch to dark mode"
              data-testid="plaza-theme-dark"
            >
              <Moon size={16} />
            </button>
          </div>
          <button
            className="hpz-topbar__cart"
            onClick={() => setCartOpen(true)}
            data-testid="plaza-cart-toggle"
          >
            <ShoppingCart size={16} />
            Cart {cartCount > 0 ? `(${cartCount})` : ""}
          </button>
        </div>
      </div>

      {/* ── Hero ────────────────────────────────────────────────── */}
      <section className="hpz-hero">
        <div className="hpz-hero__grain" />
        <div className="hpz-hero__inner">
          <span className="hpz-hero__pill">
            <span style={{ width: 6, height: 6, borderRadius: 99, background: "#F3C84B" }} />
            COMING SOON · JUNE 2026
          </span>
          <img
            src={theme === "light" ? LOGO_LIGHT : LOGO_DARK}
            alt="HustlePlaza"
            className="hpz-hero__logo"
          />
          <h1>The pantry suppliers buy from.<br/><em>Now open to everyone.</em></h1>
          <p className="hpz-hero__sub">
            HustlePlaza pools weekly orders from thousands of members so we can
            negotiate <strong>wholesale-tier pricing</strong> across 19
            categories — coffee, pantry, whitegoods, fashion, tools, baby gear,
            pet care, and more. 20–50% off retail, shipped from SE Queensland
            to your door.
          </p>
          <div className="hpz-hero__ctas">
            <a href="#waitlist" className="hpz-btn hpz-btn--primary" data-testid="plaza-hero-cta-waitlist">
              <HeartHandshake size={16} />
              Join the Waitlist · Get $25 Credit
            </a>
            <a href="#sellers" className="hpz-btn hpz-btn--ghost" data-testid="plaza-hero-cta-seller">
              <Store size={16} />
              Sell on HustlePlaza
            </a>
          </div>
        </div>
      </section>

      {/* ── Category Pills — iter254 ────────────────────────────
       *  Live shopper-facing counts of what's inside the catalogue.
       *  Rendered above the conversion strip so first-time visitors
       *  (and search engines) see the scale immediately, before the
       *  waitlist ask. Each pill scrolls to the shop grid AND applies
       *  its master-group filter, so tapping "Fashion" opens the
       *  Fashion catalogue in one gesture.
       */}
      <section
        id="category-pills"
        data-testid="plaza-category-pills"
        className="hpz-catpills"
        aria-label="Browse by category"
      >
        <div style={{ padding: "0 16px 16px" }}>
          <HustlePointsPromoBanner variant="dark" />
        </div>
        <div className="hpz-catpills__eyebrow">Browse by category</div>
        <div className="hpz-catpills__row">
          {(catGroups && catGroups.length
            ? catGroups.map((g) => ({
                group: g.group,
                count: (g.count || 0).toLocaleString("en-AU"),
              }))
            : [
              { group: "Fashion",             count: "12,340" },
              { group: "Home & Living",       count: "8,772"  },
              { group: "Sports & Outdoors",   count: "6,105"  },
              { group: "Kitchen & Whitegoods",count: "4,918"  },
              { group: "Baby & Kids",         count: "3,240"  },
              { group: "Health & Beauty",     count: "2,860"  },
              { group: "Pet Supplies",        count: "2,110"  },
              { group: "Tools & Trades",      count: "1,988"  },
              { group: "Tech & Gadgets",      count: "1,540"  },
            ]
          ).map((c) => (
            <button
              key={c.group}
              type="button"
              onClick={() => {
                chooseGroup(c.group);
                const shop = document.getElementById("shop");
                if (shop) shop.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              data-testid={`plaza-catpill-${c.group.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="hpz-catpills__pill"
              title={`${c.group} — ${c.count} products`}
            >
              <span className="hpz-catpills__label">{c.group}</span>
              <span className="hpz-catpills__count">{c.count}</span>
            </button>
          ))}
        </div>
      </section>

      {/* ── Live Conversion Strip ─────────────────────────────── */}
      {(stats || settings) && (
        <section className="hpz-conv" data-testid="plaza-conv-strip">
          {stats && (
            <div className="hpz-conv__card" data-testid="plaza-counter">
              <div className="hpz-conv__icon"><TrendingUp size={18} /></div>
              <div>
                <strong>{stats.total_members.toLocaleString("en-AU")}</strong>
                <span> founding members</span>
                {stats.members_this_week > 0 && (
                  <span className="hpz-conv__delta">+{stats.members_this_week.toLocaleString("en-AU")} this week</span>
                )}
              </div>
            </div>
          )}
          {settings && settings.scarcity_counter_enabled && settings.founding_remaining < settings.founding_cap && (
            <div className="hpz-conv__card hpz-conv__card--scarcity" data-testid="plaza-scarcity">
              <div className="hpz-conv__icon hpz-conv__icon--hot"><Flame size={18} /></div>
              <div>
                <strong>{settings.founding_remaining.toLocaleString("en-AU")}</strong>
                <span> of {settings.founding_cap.toLocaleString("en-AU")} ${settings.signup_credit_amount} founding credits remaining</span>
                <div className="hpz-conv__bar">
                  <div
                    className="hpz-conv__bar-fill"
                    style={{ width: `${100 * (1 - settings.founding_remaining / settings.founding_cap)}%` }}
                  />
                </div>
              </div>
            </div>
          )}
          {stats && settings && settings.suburb_leaderboard_enabled && stats.top_suburbs.length > 0 && (
            <div className="hpz-conv__leaderboard" data-testid="plaza-leaderboard">
              <div className="hpz-conv__leaderboard-head">
                <MapPin size={14} /> Top SE QLD suburbs joining now
              </div>
              <div className="hpz-conv__chips">
                {stats.top_suburbs.map((s) => (
                  <span key={s.suburb} className="hpz-conv__chip">
                    <strong>{s.count}</strong> · {s.suburb}
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>
      )}

      {/* ── Why Co-op ─────────────────────────────────────────── */}
      <section id="why" className="hpz-coop">
        <div className="hpz-coop__card">
          <div className="hpz-coop__icon"><Percent size={22} /></div>
          <h3 className="hpz-coop__title">Wholesale pricing for everyone</h3>
          <p className="hpz-coop__body">
            Your basket gets bundled with thousands of others. Suppliers ship one
            big order, not ten thousand small ones — and pass the savings back.
          </p>
        </div>
        <div className="hpz-coop__card">
          <div className="hpz-coop__icon"><HeartHandshake size={22} /></div>
          <h3 className="hpz-coop__title">Australian-first sourcing</h3>
          <p className="hpz-coop__body">
            Premium AU-made coffee, native honeys, small-batch chocolate, and
            artisan oils — alongside curated international staples and the kind
            of homewares & whitegoods you'd find in a Westfield flagship.
          </p>
        </div>
        <div className="hpz-coop__card">
          <div className="hpz-coop__icon"><Truck size={22} /></div>
          <h3 className="hpz-coop__title">Weekly delivery, less freight</h3>
          <p className="hpz-coop__body">
            One cooperative shipment per week to your suburb means a fraction
            of the last-mile freight (and a fraction of the cardboard).
          </p>
        </div>
      </section>

      {/* ── Top Products ────────────────────────────────────────── */}
      <section id="shop" className="hpz-section">
        <p className="hpz-section__eyebrow">Founding Members' Picks</p>
        <h2 className="hpz-section__title">Top Products</h2>
        <p className="hpz-section__sub">
          The first basket of items pre-launch. Real producers. Real pricing.
        </p>

        <div className="hpz-top" data-testid="plaza-top-strip">
          {topProducts.map((p) => (
            <ProductCard
              key={`top-${p.id}`}
              product={p}
              onAdd={() => addToCart(p.id)}
              onOpen={() => setDetail(p)}
              variant="top"
            />
          ))}
        </div>
      </section>

      {/* ── Featured Vendors rail ───────────────────────────────── */}
      {featuredVendors.length > 0 && (
        <section
          className="hpz-section"
          data-testid="plaza-featured-vendors"
          style={{ paddingTop: 0 }}
        >
          <p className="hpz-section__eyebrow">Small businesses, real people</p>
          <h2 className="hpz-section__title">Featured Vendors</h2>
          <p className="hpz-section__sub">
            The brands your neighbours are choosing. Independently-owned,
            ABN-verified, ranked by real customer reviews.
          </p>

          <div className="hpz-vendors" role="list">
            {featuredVendors.map((v) => (
              <button
                key={v.brand}
                role="listitem"
                className="hpz-vendor"
                onClick={() => {
                  setQuery(v.brand);
                  setValueChip(null);
                  document.getElementById("shop")?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid={`plaza-vendor-${v.brand.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="hpz-vendor__thumb">
                  <img src={v.img} alt={v.brand} loading="lazy" />
                </div>
                <div className="hpz-vendor__body">
                  <h4 className="hpz-vendor__name">{v.brand}</h4>
                  <div className="hpz-vendor__meta">
                    <Star size={12} fill="#F3C84B" style={{ marginRight: 4 }} />
                    {v.avgRating.toFixed(1)} · {v.count} products
                  </div>
                  {v.values.length > 0 && (
                    <div className="hpz-vendor__values">
                      {v.values.map((vk) => {
                        const c = VALUE_CHIPS.find((x) => x.key === vk);
                        return c ? (
                          <span key={vk} className="hpz-value-badge">
                            {c.label}
                          </span>
                        ) : null;
                      })}
                    </div>
                  )}
                  <div className="hpz-vendor__cta">See picks →</div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* ── Storefront grid ────────────────────────────────────── */}
      <section className="hpz-section" style={{ paddingTop: 0 }}>
        <p className="hpz-section__eyebrow">All {counts.total} Founding Items</p>
        <h2 className="hpz-section__title">Browse the Catalogue</h2>
        <p className="hpz-section__sub">
          Fuzzy-search by name, brand, master group, sub-category, or tag.
          Switch between Grid and Table view.
        </p>

        {/* Values chip row — filter by "Australian owned", "Handmade", etc. */}
        <div
          className="hpz-values"
          role="group"
          aria-label="Filter by shared values"
          data-testid="plaza-values-chips"
        >
          {VALUE_CHIPS.map((c) => (
            <button
              key={c.key}
              type="button"
              className={`hpz-values__chip ${
                valueChip === c.key ? "hpz-values__chip--on" : ""
              }`}
              onClick={() =>
                setValueChip(valueChip === c.key ? null : c.key)
              }
              disabled={valueCounts[c.key] === 0}
              data-testid={`plaza-value-chip-${c.key}`}
            >
              {c.label}
              <span className="hpz-values__count">{valueCounts[c.key]}</span>
            </button>
          ))}
          {valueChip && (
            <button
              type="button"
              className="hpz-values__clear"
              onClick={() => setValueChip(null)}
              data-testid="plaza-values-clear"
            >
              Clear
            </button>
          )}
        </div>

        <div className="hpz-store">
          {/* Sidebar (desktop) + Drawer (mobile) share the same markup */}
          <CategorySidebar
            counts={counts}
            filter={filter}
            expanded={expanded}
            onToggleGroup={toggleGroup}
            onChooseAll={chooseAll}
            onChooseGroup={chooseGroup}
            onChooseSub={chooseSub}
          />

          {/* Right column */}
          <div>
            <div className="hpz-toolbar">
              <button
                className="hpz-toolbar__filters-btn"
                onClick={() => setFiltersOpen(true)}
                data-testid="plaza-filters-mobile-btn"
              >
                <SlidersHorizontal size={16} />
                <span>Filters</span>
              </button>
              <div className="hpz-search">
                <Search size={16} />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products, brands, master groups…"
                  data-testid="plaza-search-input"
                />
              </div>
              <select
                className="hpz-sort"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                data-testid="plaza-sort-select"
              >
                {SORT_OPTIONS.map((o) => (
                  <option key={o.id} value={o.id}>{o.label}</option>
                ))}
              </select>
              <div className="hpz-viewtoggle" role="group" aria-label="View mode">
                <button
                  className={view === "grid" ? "on" : ""}
                  onClick={() => { setView("grid"); setViewLocked(true); }}
                  title="Grid view"
                  data-testid="plaza-view-grid"
                >
                  <LayoutGrid size={16} />
                </button>
                <button
                  className={view === "table" ? "on" : ""}
                  onClick={() => { setView("table"); setViewLocked(true); }}
                  title="Table view"
                  data-testid="plaza-view-table"
                >
                  <Rows3 size={16} />
                </button>
              </div>
            </div>

            <p className="hpz-result-count" data-testid="plaza-result-count">
              {filtered.length} {filtered.length === 1 ? "item" : "items"}
              {filter.type !== "all" && ` in ${filterLabel}`}
            </p>

            {filtered.length === 0 ? (
              <div className="hpz-empty">
                No products match "{query}"{filter.type !== "all" && ` in ${filterLabel}`}.
                <br/>Try a different search or clear the filters.
              </div>
            ) : view === "grid" ? (
              <div className="hpz-grid" data-testid="plaza-grid">
                {filtered.map((p) => (
                  <ProductCard
                    key={p.id}
                    product={p}
                    onAdd={() => addToCart(p.id)}
                    onOpen={() => setDetail(p)}
                  />
                ))}
              </div>
            ) : (
              <ProductTable
                products={filtered}
                onAdd={addToCart}
                onOpen={setDetail}
              />
            )}
          </div>
        </div>
      </section>

      {/* ── Waitlist ─────────────────────────────────────────────── */}
      <section id="waitlist" className="hpz-waitlist" data-testid="plaza-waitlist">
        {wlOk ? (
          <div className="hpz-form__ok" data-testid="plaza-waitlist-success">
            <CheckCircle2 size={36} style={{ color: "#F3C84B", marginBottom: 12 }} />
            <h3 className="hpz-success__title">
              {wlBuyer === "seller" ? (
                <>Application received. <em>Welcome to the founding vendor list.</em></>
              ) : (
                <>You're in. <em>${wlResponse?.credit_awarded ?? settings?.signup_credit_amount ?? 25} founding credit reserved.</em></>
              )}
            </h3>
            {wlResponse?.ref_code && wlBuyer !== "seller" && settings && (
              <>
                <p className="hpz-success__sub">
                  Refer {settings.referral_threshold} friends with your link below and
                  we'll add another <strong>${settings.referral_credit_amount}</strong> to your Plaza
                  account — usable on any order from launch day.
                </p>
                <div className="hpz-success__ref">
                  <input
                    type="text"
                    readOnly
                    value={refLink}
                    onFocus={(e) => e.target.select()}
                    data-testid="plaza-ref-link"
                  />
                  <button
                    type="button"
                    onClick={copyRefLink}
                    className={refCopied ? "copied" : ""}
                    data-testid="plaza-ref-copy"
                  >
                    {refCopied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                    {refCopied ? "Copied!" : "Copy link"}
                  </button>
                </div>
                <p className="hpz-success__hint">
                  Tip: paste this in your favourite group chat. Every friend who
                  joins from your link gets their own ${settings.signup_credit_amount} credit too.
                </p>
              </>
            )}
            {wlBuyer === "seller" && (
              <p className="hpz-success__sub">
                Our vendor team will reach out within one business day to set
                up your supplier profile, pricing tiers, and onboarding call.
              </p>
            )}
          </div>
        ) : (
          <>
            <h2>Join the Founding-Member <em>waitlist.</em></h2>
            <p className="hpz-waitlist__sub">
              Be first in. Get a $25 credit on your first basket. Help us shape
              what we stock.
            </p>

            <form className="hpz-form" onSubmit={submitWaitlist}>
              <div className="hpz-form__full">
                <label>I am a…</label>
                <div className="hpz-cats">
                  <span
                    className={`hpz-cats__chip ${wlBuyer === "consumer" ? "on" : ""}`}
                    onClick={() => setWlBuyer("consumer")}
                    data-testid="plaza-buyer-consumer"
                  >Consumer / household</span>
                  <span
                    className={`hpz-cats__chip ${wlBuyer === "seller" ? "on" : ""}`}
                    onClick={() => setWlBuyer("seller")}
                    data-testid="plaza-buyer-seller"
                  >Producer wanting to sell</span>
                </div>
              </div>

              <div>
                <label>Email *</label>
                <input
                  type="email" required
                  value={wlEmail}
                  onChange={(e) => setWlEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="plaza-form-email"
                />
              </div>
              <div>
                <label>{wlBuyer === "seller" ? "Brand / Business Name *" : "Household / Business Name *"}</label>
                <input
                  type="text" required
                  value={wlBusiness}
                  onChange={(e) => setWlBusiness(e.target.value)}
                  placeholder={wlBuyer === "seller" ? "e.g. Brookfarm" : "The Smith Family"}
                  data-testid="plaza-form-business"
                />
              </div>
              <div className="hpz-form__full">
                <label>Suburb *</label>
                <input
                  type="text" required
                  value={wlSuburb}
                  onChange={(e) => setWlSuburb(e.target.value)}
                  placeholder="e.g. Paddington QLD 4064"
                  data-testid="plaza-form-suburb"
                />
              </div>

              <div className="hpz-form__full">
                <label>{wlBuyer === "seller" ? "Categories we'd find your products in" : "What categories interest you?"}</label>
                <div className="hpz-cats">
                  {MASTER_GROUPS.map((g) => (
                    <span
                      key={g.name}
                      className={`hpz-cats__chip ${wlCats.includes(g.name) ? "on" : ""}`}
                      onClick={() => toggleCat(g.name)}
                      data-testid={`plaza-form-cat-${g.tag}`}
                    >
                      {g.name}
                    </span>
                  ))}
                </div>
              </div>

              {wlErr && (
                <div className="hpz-form__full" style={{ color: "#FF9F87", fontSize: 13 }}>
                  {wlErr}
                </div>
              )}

              <button
                type="submit"
                className="hpz-form__submit hpz-form__full"
                disabled={wlBusy}
                data-testid="plaza-form-submit"
              >
                {wlBusy
                  ? "Submitting…"
                  : wlBuyer === "seller"
                    ? "Apply to sell on HustlePlaza →"
                    : "Reserve my $25 founding credit →"}
              </button>
              {wlBuyer === "seller" && (
                <Link
                  to="/signup/plaza_vendor"
                  className="hpz-form__full"
                  data-testid="plaza-form-vendor-signup"
                  style={{
                    display: "block",
                    textAlign: "center",
                    marginTop: 8,
                    color: "#f4c26e",
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.5,
                    textDecoration: "underline",
                  }}
                >
                  Or complete the full vendor signup (ABN-verified) →
                </Link>
              )}
              <p className="hpz-form__legal hpz-form__full" data-testid="plaza-form-legal">
                By submitting, you become a member of the Hustle Group cooperative
                and agree to our{" "}
                <a href="/plaza/terms" target="_blank" rel="noopener noreferrer">
                  Cooperative Terms
                </a>
                {" "}including communications by email, SMS, push, and WhatsApp.
                You can unsubscribe from any channel at any time.
              </p>
            </form>
          </>
        )}
      </section>

      {/* ── Seller CTA strip ─────────────────────────────────────── */}
      <section id="sellers" className="hpz-seller" data-testid="plaza-seller">
        <div className="hpz-seller__text">
          <h3>You make something we'd love to stock?</h3>
          <p>
            We're onboarding ~120 founding vendors before June launch. AU
            small-batch, ethically-sourced imports, and B-Corp suppliers get
            priority.
          </p>
        </div>
        <a
          href="#waitlist"
          className="hpz-btn hpz-btn--primary"
          onClick={() => setWlBuyer("seller")}
          data-testid="plaza-seller-cta"
        >
          <Store size={16} />
          Apply to Sell
        </a>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────── */}
      <section id="faq" className="hpz-section">
        <p className="hpz-section__eyebrow">Common Questions</p>
        <h2 className="hpz-section__title">FAQ</h2>
        <div className="hpz-faq" data-testid="plaza-faq">
          {FAQ_ITEMS.map((f, i) => (
            <div
              key={i}
              className={`hpz-faq__item ${faqOpen === i ? "open" : ""}`}
              onClick={() => setFaqOpen(faqOpen === i ? -1 : i)}
              data-testid={`plaza-faq-item-${i}`}
            >
              <div className="hpz-faq__q">
                {f.q}
                <Plus size={20} />
              </div>
              <div className="hpz-faq__a">{f.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────── */}
      <footer className="hpz-foot">
        <p>
          HustlePlaza is a product of HustlePlate. © 2026.
          {" · "}
          <a href="/">Back to HustlePlate</a>
          {" · "}
          <a href="/plaza/terms" data-testid="plaza-footer-terms">Cooperative Terms</a>
          {" · "}
          <a href="/pitch/investor">Investor Pitch</a>
        </p>
      </footer>

      {/* ── Mobile Filters Drawer ──────────────────────────────── */}
      <div
        className={`hpz-drawer-back ${filtersOpen ? "open" : ""}`}
        onClick={() => setFiltersOpen(false)}
      />
      <aside
        className={`hpz-filters-drawer ${filtersOpen ? "open" : ""}`}
        data-testid="plaza-filters-drawer"
        aria-hidden={!filtersOpen}
      >
        <div className="hpz-drawer__head">
          <h3>Categories</h3>
          <button
            className="hpz-drawer__close"
            onClick={() => setFiltersOpen(false)}
            data-testid="plaza-filters-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="hpz-drawer__body">
          <CategorySidebar
            counts={counts}
            filter={filter}
            expanded={expanded}
            onToggleGroup={toggleGroup}
            onChooseAll={chooseAll}
            onChooseGroup={chooseGroup}
            onChooseSub={chooseSub}
            embedded
          />
        </div>
      </aside>

      {/* ── Cart Drawer ────────────────────────────────────────── */}
      <div
        className={`hpz-drawer-back ${cartOpen ? "open" : ""}`}
        onClick={() => setCartOpen(false)}
      />
      <aside
        className={`hpz-drawer ${cartOpen ? "open" : ""}`}
        data-testid="plaza-cart-drawer"
        aria-hidden={!cartOpen}
      >
        <div className="hpz-drawer__head">
          <h3>Your Basket</h3>
          <button
            className="hpz-drawer__close"
            onClick={() => setCartOpen(false)}
            data-testid="plaza-cart-close"
          >
            <X size={20} />
          </button>
        </div>
        <div className="hpz-drawer__body">
          {cart.length === 0 ? (
            <div className="hpz-drawer__empty">
              <ShoppingCart size={40} style={{ margin: "0 auto 12px", display: "block", opacity: 0.4 }} />
              Your basket is empty.<br />
              Browse the catalogue and tap "Add" on anything you like.
            </div>
          ) : (
            cart.map((r) => {
              const p = PLAZA_PRODUCTS.find((x) => x.id === r.id);
              if (!p) return null;
              return (
                <div key={r.id} className="hpz-drawer__row">
                  <img src={p.img} alt={p.name} loading="lazy" />
                  <div>
                    <div className="hpz-drawer__row-name">{p.name}</div>
                    <div className="hpz-drawer__qty">
                      <button onClick={() => setQty(r.id, -1)} data-testid={`plaza-cart-dec-${r.id}`}>
                        <Minus size={12} />
                      </button>
                      <span style={{ fontWeight: 600 }}>{r.qty}</span>
                      <button onClick={() => setQty(r.id, +1)} data-testid={`plaza-cart-inc-${r.id}`}>
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  <div className="hpz-drawer__row-price">
                    ${fmtMoney(p.price * r.qty)}
                  </div>
                </div>
              );
            })
          )}
        </div>
        {cart.length > 0 && (
          <div className="hpz-drawer__foot">
            <div className="hpz-drawer__total">
              <span>Subtotal</span>
              <b>${fmtMoney(cartTotal)}</b>
            </div>
            <button
              className="hpz-drawer__check"
              onClick={() => {
                setCartOpen(false);
                if (settings?.cart_abandon_email_enabled !== false) {
                  setSaveModalOpen(true);
                } else {
                  document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
                }
              }}
              data-testid="plaza-cart-checkout"
            >
              Save my basket for {settings?.launch_label ?? "June 2026"}
              <ArrowRight size={16} style={{ marginLeft: 6 }} />
            </button>
            <p className="hpz-drawer__note">
              We're in preview mode. Email yourself this basket and we'll deliver it at launch.
            </p>
          </div>
        )}
      </aside>

      {/* ── Cart-Save Modal (cart-abandon recovery) ─────────────── */}
      {saveModalOpen && (
        <>
          <div
            className="hpz-drawer-back open"
            onClick={() => { setSaveModalOpen(false); setSaveDone(false); }}
            data-testid="plaza-save-backdrop"
          />
          <div className="hpz-savemodal" role="dialog" aria-modal="true" data-testid="plaza-save-modal">
            <button
              className="hpz-savemodal__close"
              onClick={() => { setSaveModalOpen(false); setSaveDone(false); }}
              data-testid="plaza-save-close"
            ><X size={20} /></button>
            {saveDone ? (
              <div className="hpz-savemodal__done">
                <CheckCircle2 size={42} style={{ color: "#F3C84B", marginBottom: 12 }} />
                <h3>Basket saved.</h3>
                <p>
                  We'll email <strong>{saveEmail}</strong> when {settings?.launch_label ?? "the marketplace"} opens.
                  Today's pricing is locked.
                </p>
                <button
                  className="hpz-savemodal__action"
                  onClick={() => {
                    setSaveModalOpen(false);
                    document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" });
                  }}
                  data-testid="plaza-save-after-join"
                >
                  Also join the waitlist for {settings?.signup_credit_amount ?? 25} bonus credit
                </button>
              </div>
            ) : (
              <>
                <div className="hpz-savemodal__icon"><Mail size={28} /></div>
                <h3>Email yourself this basket</h3>
                <p>
                  We'll lock today's prices on the {cart.length} item{cart.length === 1 ? "" : "s"} in your basket and
                  send you a reminder the day {settings?.launch_label ?? "we"} go live.
                </p>
                <form onSubmit={saveBasketForLater} className="hpz-savemodal__form">
                  <input
                    type="email"
                    required
                    value={saveEmail}
                    onChange={(e) => setSaveEmail(e.target.value)}
                    placeholder="you@example.com"
                    data-testid="plaza-save-email"
                  />
                  <button
                    type="submit"
                    disabled={saveBusy}
                    data-testid="plaza-save-submit"
                  >
                    {saveBusy ? "Saving…" : "Save my basket →"}
                  </button>
                </form>
                <p className="hpz-savemodal__legal">
                  No spam. We'll only email about this basket — unsubscribe in one tap.
                </p>
              </>
            )}
          </div>
        </>
      )}

      {/* ── Product Detail Modal ────────────────────────────────── */}
      <ProductDetailModal
        product={detail}
        onClose={() => setDetail(null)}
        onAdd={addToCart}
      />

      {/* ── Non-member join gate (fires on add-to-cart) ─────────── */}
      <JoinGateModal
        open={joinGateOpen}
        onClose={() => setJoinGateOpen(false)}
        onJoin={() => {
          setJoinGateOpen(false);
          navigate("/register?returnTo=/plaza/shop&source=cart");
        }}
        onSignIn={() => {
          setJoinGateOpen(false);
          navigate("/login?returnTo=/plaza/shop");
        }}
      />
      <SuiteFooter />
    </div>
  );
}

/* ─── Sidebar (accordion) ──────────────────────────────────────────
   Shared between desktop sticky-side column and mobile drawer.
   When embedded in the drawer, testids are prefixed `plaza-drawer-` so
   automation can target the visible sidebar unambiguously even though
   both render in the DOM at the same time.                              */
function CategorySidebar({
  counts, filter, expanded, embedded,
  onToggleGroup, onChooseAll, onChooseGroup, onChooseSub,
}) {
  const tid = (suffix) => `plaza-${embedded ? "drawer-" : ""}${suffix}`;
  return (
    <aside
      className={`hpz-side ${embedded ? "embedded" : ""}`}
      data-testid={tid("sidebar")}
    >
      {!embedded && <p className="hpz-side__title">Categories</p>}
      <ul className="hpz-side__list">
        <li
          className={`hpz-side__row ${filter.type === "all" ? "active" : ""}`}
          onClick={onChooseAll}
          data-testid={tid("cat-all")}
        >
          <span>All Products</span>
          <span className="hpz-side__count">{counts.total}</span>
        </li>
        {MASTER_GROUPS.map((g) => {
          const open = expanded.has(g.name);
          const active = filter.type === "group" && filter.value === g.name;
          const groupCount = counts.groupCounts[g.name] || 0;
          return (
            <li key={g.name} className="hpz-side__group">
              <div
                className={`hpz-side__row hpz-side__row--group ${active ? "active" : ""}`}
                data-testid={tid(`group-${g.tag}`)}
              >
                <span
                  className="hpz-side__group-toggle"
                  onClick={() => onToggleGroup(g.name)}
                  data-testid={tid(`group-toggle-${g.tag}`)}
                >
                  {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <span
                  className="hpz-side__group-name"
                  onClick={() => onChooseGroup(g.name)}
                >
                  {g.name}
                </span>
                <span className="hpz-side__count">{groupCount}</span>
              </div>
              {open && (
                <ul className="hpz-side__sublist">
                  {g.subs.map((sub) => {
                    const subActive = filter.type === "sub" && filter.value === sub;
                    const subCount = counts.subCounts[sub] || 0;
                    if (subCount === 0) return null;
                    return (
                      <li
                        key={sub}
                        className={`hpz-side__row hpz-side__row--sub ${subActive ? "active" : ""}`}
                        onClick={() => onChooseSub(sub)}
                        data-testid={tid(`sub-${sub.replace(/[^A-Za-z0-9]/g, "")}`)}
                      >
                        <span>{sub}</span>
                        <span className="hpz-side__count">{subCount}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
}

/* ─── Product Card (grid view) ───────────────────────────────────── */
function ProductCard({ product, onAdd, onOpen, variant }) {
  const { name, brand, price, was, rating, reviews, img, in_stock, is_new } = product;
  const savings = was ? Math.round(((was - price) / was) * 100) : 0;
  const prefix = variant === "top" ? "plaza-top-" : "plaza-";
  const values = valuesFor(product).slice(0, 2);   // small badges — cap at 2

  return (
    <article
      className="hpz-top__card"
      onClick={() => onOpen && onOpen()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onOpen && onOpen(); }}
      data-testid={`${prefix}product-${product.id}`}
    >
      <div className="hpz-top__img-wrap">
        <img src={img} alt={name} className="hpz-top__img" loading="lazy" />
        {is_new && <span className="hpz-badge hpz-badge--new">NEW</span>}
        {savings > 0 && <span className="hpz-badge hpz-badge--coop">-{savings}%</span>}
      </div>
      <div className="hpz-top__body">
        <p className="hpz-top__brand">{brand}</p>
        <h4 className="hpz-top__name">{name}</h4>
        {values.length > 0 && (
          <div
            className="hpz-top__values"
            data-testid={`${prefix}values-${product.id}`}
          >
            {values.map((v) => (
              <span key={v.key} className="hpz-value-badge">
                {v.label}
              </span>
            ))}
          </div>
        )}
        <div className="hpz-top__rating">
          <Star size={13} fill="#F3C84B" />
          <span>{rating.toFixed(1)}</span>
          <span style={{ color: "var(--hpz-faint)" }}>· {reviews} reviews</span>
        </div>
        <div className="hpz-top__prices">
          {was && <span className="hpz-top__rrp">${fmtMoney(was)}</span>}
          <span className="hpz-top__price">${fmtMoney(price)}</span>
          {savings > 0 && <span className="hpz-top__save">Save {savings}%</span>}
        </div>
        <button
          className="hpz-top__add"
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          disabled={!in_stock}
          data-testid={`${prefix}add-${product.id}`}
        >
          {in_stock ? (<><Plus size={14} /> Add to basket</>) : "Out of stock"}
        </button>
      </div>
    </article>
  );
}

/* ─── Product Table (table view) ─────────────────────────────────── */
function ProductTable({ products, onAdd, onOpen }) {
  return (
    <div className="hpz-tablewrap" data-testid="plaza-table">
      <table className="hpz-table">
        <thead>
          <tr>
            <th></th>
            <th>Product</th>
            <th>Brand</th>
            <th>Category</th>
            <th>Rating</th>
            <th>RRP</th>
            <th>Co-op Price</th>
            <th>Save</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {products.map((p) => {
            const savings = p.was ? Math.round(((p.was - p.price) / p.was) * 100) : 0;
            return (
              <tr
                key={p.id}
                onClick={() => onOpen && onOpen(p)}
                data-testid={`plaza-row-${p.id}`}
              >
                <td className="hpz-table__thumb">
                  <img src={p.img} alt={p.name} loading="lazy" />
                </td>
                <td className="hpz-table__name">
                  <div className="hpz-table__name-text">{p.name}</div>
                  <div className="hpz-table__sub-text">{p.sub_category}</div>
                </td>
                <td>{p.brand}</td>
                <td className="hpz-table__cell-mute">{p.master_group}</td>
                <td>
                  <span className="hpz-table__rating">
                    <Star size={11} fill="#F3C84B" /> {p.rating.toFixed(1)}
                  </span>
                </td>
                <td className="hpz-table__rrp">{p.was ? `$${fmtMoney(p.was)}` : "—"}</td>
                <td className="hpz-table__price">${fmtMoney(p.price)}</td>
                <td>{savings > 0 ? <span className="hpz-table__save">-{savings}%</span> : "—"}</td>
                <td>
                  <button
                    className="hpz-table__add"
                    onClick={(e) => { e.stopPropagation(); onAdd(p.id); }}
                    disabled={!p.in_stock}
                    data-testid={`plaza-table-add-${p.id}`}
                  >
                    {p.in_stock ? "Add" : "Out"}
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/* ─── Product Detail Modal ───────────────────────────────────────── */
function ProductDetailModal({ product, onClose, onAdd }) {
  if (!product) return null;
  const { name, brand, price, was, rating, reviews, img, in_stock,
          master_group, sub_category, tags, description, units_left } = product;
  const savings = was ? Math.round(((was - price) / was) * 100) : 0;

  return (
    <>
      <div
        className="hpz-drawer-back open"
        onClick={onClose}
        data-testid="plaza-detail-backdrop"
      />
      <div
        className="hpz-detail"
        role="dialog"
        aria-modal="true"
        aria-label={`${brand} ${name}`}
        data-testid="plaza-detail-modal"
      >
        <button
          className="hpz-detail__close"
          onClick={onClose}
          data-testid="plaza-detail-close"
        >
          <X size={22} />
        </button>

        <div className="hpz-detail__grid">
          <div className="hpz-detail__media">
            <img src={img} alt={name} />
            {savings > 0 && (
              <span className="hpz-badge hpz-badge--coop hpz-detail__badge">-{savings}%</span>
            )}
          </div>

          <div className="hpz-detail__body">
            <p className="hpz-detail__crumb">
              {master_group} <span style={{ opacity: 0.5, margin: "0 6px" }}>›</span> {sub_category}
            </p>
            <p className="hpz-detail__brand">{brand}</p>
            <h2 className="hpz-detail__title">{name}</h2>

            <div className="hpz-detail__rating">
              <Star size={15} fill="#F3C84B" />
              <strong>{rating.toFixed(1)}</strong>
              <span style={{ color: "var(--hpz-faint)" }}>· {reviews.toLocaleString("en-AU")} reviews</span>
            </div>

            <div className="hpz-detail__price-row">
              {was && <span className="hpz-detail__rrp">${fmtMoney(was)} RRP</span>}
              <span className="hpz-detail__price">${fmtMoney(price)}</span>
              {savings > 0 && (
                <span className="hpz-detail__save">You save ${fmtMoney(was - price)} ({savings}%)</span>
              )}
            </div>

            <p className="hpz-detail__stock">
              {in_stock
                ? <><span className="hpz-detail__dot hpz-detail__dot--in" /> In stock — {units_left} units available this week</>
                : <><span className="hpz-detail__dot hpz-detail__dot--out" /> Currently out of stock</>}
            </p>

            <div className="hpz-detail__desc">
              {description.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
            </div>

            {tags && tags.length > 0 && (
              <div className="hpz-detail__tags">
                {tags.map((t) => <span key={t} className="hpz-detail__tag">#{t}</span>)}
              </div>
            )}

            <button
              className="hpz-detail__add"
              onClick={() => { onAdd(product.id); onClose(); }}
              disabled={!in_stock}
              data-testid="plaza-detail-add"
            >
              {in_stock ? (
                <>
                  <Plus size={18} /> Add to basket — ${fmtMoney(price)}
                </>
              ) : "Out of stock"}
            </button>
            <p className="hpz-detail__note">
              Full vendor description and specifications coming soon as part of our drop-shipper integration.
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
